import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select
} from "@mui/material";
import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { TypeDeviceStatusBr } from "../enum/typeDeviceStatusEnum";
import { SectionDevice } from "../style/DeviceCreateModal.styled";

import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { usePageToast } from "../../hooks/usePageToast";
import { InputText } from "../style/ClientCreateModal.styled";
import PageToaster from "../toaster/PageToaster";

interface DeviceCreateModalProps {
  open: boolean;
  onClose: () => void;
  onDeviceCreated: () => void;
  client_id: number;
  device?: any;
  order_id?: string;
}

const DeviceCreateModal: React.FC<DeviceCreateModalProps> = ({
  open,
  onClose,
  onDeviceCreated,
  client_id,
  device,
  order_id
}) => {
  const [deviceName, setDeviceName] = useState("");
  const [deviceBrand, setDeviceBrand] = useState("");
  const [deviceDefect, setDeviceDefect] = useState("");

  const [deviceInvoiceNumber, setDeviceInvoiceNumber] = useState<string>("");
  const [deviceInvoiceDate, setDeviceInvoiceDate] = useState<string>("");
  const [deviceObservation, setDeviceObservation] = useState<string>("");

  const [deviceModel, setDeviceModel] = useState("");
  const [deviceSerialNumber, setDeviceSerialNumber] = useState("");
  const [deviceWarrantyEndDate, setDeviceWarrantyEndDate] = useState("");

  const [isUpdate, setIsUpdate] = useState(false);
  const [orderId, setOrderId] = useState<string | null>();
  const [serviceType, setServiceType] =
    useState<keyof typeof TypeDeviceStatusBr>("WAITING");

  // validação derivada
  const [triedSubmit, setTriedSubmit] = useState(false);
  const isEmpty = (s: string) => s.trim() === "";

  const containerId = "toaster-device-create-modal";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const handleHttpError = makeToasterErrorHandler(autoError);

  const invalidName = triedSubmit && isEmpty(deviceName);
  const invalidBrand = triedSubmit && isEmpty(deviceBrand);
  const invalidModel = triedSubmit && isEmpty(deviceModel);
  const invalidSerial = triedSubmit && isEmpty(deviceSerialNumber);

  useEffect(() => {
    if (device) {
      setDeviceName(device.device_name ?? "");
      setDeviceBrand(device.device_brand ?? "");
      setDeviceModel(device.device_model ?? "");
      setDeviceDefect(device.device_defect ?? "");
      setDeviceInvoiceNumber(device.device_invoice_number ?? "");
      setDeviceInvoiceDate(
        device.device_invoice_date
          ? device.device_invoice_date.slice(0, 10)
          : ""
      );
      setDeviceObservation(device.device_observation ?? "");
      setDeviceSerialNumber(device.device_serial_number ?? "");
      setDeviceWarrantyEndDate(
        device.device_warranty_end_date
          ? device.device_warranty_end_date.slice(0, 10)
          : ""
      );
      setIsUpdate(true);
      setOrderId(order_id ?? null);
      setTriedSubmit(false);
    } else {
      handleClose(); // Limpa os campos se não houver equipamento
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device]);

  const handleClose = () => {
    setDeviceName("");
    setDeviceBrand("");
    setDeviceModel("");
    setDeviceDefect("");
    setDeviceInvoiceNumber("");
    setDeviceSerialNumber("");
    setDeviceInvoiceDate("");
    setDeviceObservation("");
    setDeviceWarrantyEndDate("");
    setIsUpdate(false);
    setOrderId(order_id ?? null);
    setTriedSubmit(false);
  };

  const handleCancel = () => {
    handleClose();
    onClose();
  };

  const handleSubmit = async () => {
    setTriedSubmit(true);

    if (invalidName || invalidBrand || invalidModel || invalidSerial) {
      autoError("Preencha todos os campos obrigatórios.");
      return;
    }

    const payload: any = {
      device_name: deviceName.trim(),
      device_brand: deviceBrand.trim(),
      device_model: deviceModel.trim(),
      device_serial_number: deviceSerialNumber.trim(),
      device_defect: deviceDefect.trim(),
      device_invoice_number: deviceInvoiceNumber.trim(),
      device_invoice_date: deviceInvoiceDate || null,
      device_observation: deviceObservation.trim(),
      device_warranty_end_date: deviceWarrantyEndDate || null,
      client_id,
      device_condition: serviceType
    };

    console.log("payload", payload);
    try {
      if (isUpdate && device) {
        await api.put(`/devices/${device.device_id}`, payload);
        autoSuccess("Equipamento atualizado com sucesso!");
      } else {
        const current_device = await api.post("/devices", payload);

        if (orderId) {
          await api.post(
            `/work-order/${orderId}/devices/${current_device.data.device_id}`,
            {}
          );
        }
        autoSuccess("Equipamento cadastrado com sucesso!");
      }

      onDeviceCreated();
      handleClose();
      onClose();
    } catch (error) {
      handleHttpError(error);
      console.error("Erro ao salvar equipamento:", error);
    }
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal
        open={open}
        onClose={handleCancel}
        aria-labelledby="create-device-modal"
      >
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "var(--surface)",
            border: "1px solid var(--border-strong)",
            color: "var(--text)",
            padding: "20px",
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            width: "70vw",
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
          <h5
            style={{
              color: "#703",
              position: "absolute",
              top: "10px",
              right: "10px"
            }}
          >
            Campos com * são obrigatórios
          </h5>
          <h2>{isUpdate ? "Editar Equipamento" : "Novo Equipamento"}</h2>

          <SectionDevice>
            <InputText
              fullWidth
              label="Nome do Equipamento"
              required
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              margin="normal"
              error={invalidName}
              $invalid={invalidName}
            />
            <InputText
              fullWidth
              label="Marca"
              required
              value={deviceBrand}
              onChange={(e) => setDeviceBrand(e.target.value)}
              margin="normal"
              error={invalidBrand}
              $invalid={invalidBrand}
            />
            <InputText
              fullWidth
              label="Modelo"
              required
              value={deviceModel}
              onChange={(e) => setDeviceModel(e.target.value)}
              margin="normal"
              error={invalidModel}
              $invalid={invalidModel}
            />
          </SectionDevice>

          <SectionDevice>
            <InputText
              fullWidth
              label="Número da Nota fiscal"
              value={deviceInvoiceNumber}
              onChange={(e) => setDeviceInvoiceNumber(e.target.value)}
              margin="normal"
            />
            <InputText
              fullWidth
              label="Data da Nota fiscal"
              type="date"
              value={deviceInvoiceDate}
              onChange={(e) => setDeviceInvoiceDate(e.target.value)}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <InputText
              fullWidth
              label="Fim da Garantia"
              type="date"
              value={deviceWarrantyEndDate}
              onChange={(e) => setDeviceWarrantyEndDate(e.target.value)}
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Status do conserto</InputLabel>
              <Select
                value={serviceType}
                label="Status do conserto"
                onChange={(e) =>
                  setServiceType(
                    e.target.value as keyof typeof TypeDeviceStatusBr
                  )
                }
              >
                {Object.entries(TypeDeviceStatusBr).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </SectionDevice>

          <SectionDevice>
            <InputText
              fullWidth
              label="Número de Série"
              required
              value={deviceSerialNumber}
              onChange={(e) => setDeviceSerialNumber(e.target.value)}
              margin="normal"
              error={invalidSerial}
              $invalid={invalidSerial}
            />
            <InputText
              fullWidth
              label="Defeito informado"
              value={deviceDefect}
              onChange={(e) => setDeviceDefect(e.target.value)}
              margin="normal"
            />
            <InputText
              fullWidth
              label="Observação"
              value={deviceObservation}
              onChange={(e) => setDeviceObservation(e.target.value)}
              margin="normal"
            />
          </SectionDevice>

          <div style={{ marginTop: "20px", textAlign: "right" }}>
            <Button
              onClick={handleCancel}
              style={{ marginRight: "10px" }}
              color="secondary"
            >
              Cancelar
            </Button>
            <Button onClick={handleSubmit} color="primary" variant="contained">
              {isUpdate ? "Actualizar" : "Cadastrar"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DeviceCreateModal;
